/**
 * What to offer in the "your first name or nickname" field after an OAuth
 * sign-in.
 *
 * Apple and Google hand over the legal full name ("Sven Kaiser"), which is the
 * wrong answer to that question — but offering nothing makes every OAuth user
 * type a name the provider already sent. So the first part is proposed, and it
 * stays editable.
 *
 * Shortening happens ONLY while the display name is still the provider's: once
 * someone has chosen their own, it is returned untouched. Otherwise a chosen
 * nickname of two words ("Sveni Boy") would silently lose its second half on a
 * later visit to this step.
 *
 * @example
 * suggestedDisplayName('Sven Kaiser', 'Sven Kaiser')  // 'Sven'   — still the provider's
 * suggestedDisplayName('Sveni Boy', 'Sven Kaiser')    // 'Sveni Boy' — chosen, left alone
 * suggestedDisplayName('mashmx', 'Sven Kaiser')       // 'mashmx'
 * suggestedDisplayName(null, 'Sven Kaiser')           // null
 */
export function suggestedDisplayName(
  fullName: string | null | undefined,
  providerName: string | null | undefined,
): string | null {
  if (!fullName?.trim()) return null
  if (!providerName || fullName.trim() !== providerName.trim()) return fullName
  return firstNameFrom(fullName)
}

/**
 * The first whitespace-separated part of a name, or null if there is none.
 *
 * @example
 * firstNameFrom('Anna Maria von Beispiel')  // 'Anna'
 * firstNameFrom('  Ada  ')                  // 'Ada'
 */
export function firstNameFrom(fullName: string | null | undefined): string | null {
  if (!fullName) return null
  // Any run of whitespace, including the non-breaking space that a copied name
  // tends to carry along.
  const first = fullName.trim().split(/\s+/)[0]
  return first || null
}
